// Transaction file DAO — data access for transaction documents.
// Used by both FE and edge functions (drive-upload, drive-move, sync-drive-documents).

import { BaseDao, type SupabaseClient, type InvalidateFn, type MutationOptions } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';
import type { TransactionFileRow, TransactionFileInsert } from './types.ts';

// ── TransactionFileDao ──────────────────────────────────────────────────

export class TransactionFileDao extends BaseDao<TransactionFileRow> {
  protected readonly dependentResources = [
    'transaction_files',
  ];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'transaction_files', invalidate);
  }

  /** Count files for a transaction. */
  async countByTransaction(transactionId: number): Promise<number> {
    const { count, error } = await this.sb
      .from('transaction_files')
      .select('id', { count: 'exact', head: true })
      .eq('transaction_id', transactionId);
    if (error) throw error;
    return count ?? 0;
  }

  /** Get file metadata needed for email attachments (minimal select). */
  async getForAttachment(fileIds: number[]): Promise<Pick<TransactionFileRow, 'id' | 'drive_file_id' | 'mime_type' | 'file_name'>[]> {
    if (fileIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('transaction_files')
      .select('id, drive_file_id, mime_type, file_name')
      .in('id', fileIds);
    if (error) throw error;
    return (data as Pick<TransactionFileRow, 'id' | 'drive_file_id' | 'mime_type' | 'file_name'>[]) ?? [];
  }

  /** Get existing drive_file_ids for a transaction (for idempotency checks). */
  async getExistingDriveFileIds(transactionId: number): Promise<Set<string>> {
    const { data, error }: { data: { drive_file_id: string }[] | null; error: PostgrestError | null } = await this.sb
      .from('transaction_files')
      .select('drive_file_id')
      .eq('transaction_id', transactionId);
    if (error) throw error;
    return new Set((data ?? []).map((r) => r.drive_file_id));
  }

  /** Bulk insert files. */
  async bulkInsert(files: TransactionFileInsert[], opts?: MutationOptions): Promise<TransactionFileRow[]> {
    if (files.length === 0) return [];
    const { data, error } = await this.sb
      .from('transaction_files')
      .insert(files as Record<string, unknown>[])
      .select('*');
    if (error) throw error;
    this.handleInvalidation(opts);
    return (data as TransactionFileRow[]) ?? [];
  }

  /** Update file metadata (review_status, is_latest, folder, etc.). */
  async updateFile(fileId: number, update: Partial<TransactionFileRow>, opts?: MutationOptions): Promise<TransactionFileRow> {
    const { data, error }: { data: TransactionFileRow | null; error: PostgrestError | null } = await this.sb
      .from('transaction_files')
      .update(update as Record<string, unknown>)
      .eq('id', fileId)
      .select('*')
      .single();
    if (error) throw error;
    this.handleInvalidation(opts);
    // `.single()` reports a missing row through `error`, so `data` is set here.
    return data as TransactionFileRow;
  }
}
